/**
 * PageMasthead — compact inner-page masthead system.
 * Replaces the giant py-24/32 hero mastheads (large title + blank zone) with a
 * deliberate, economical header: page identity (eyebrow) + heading + intro,
 * minimal wasted vertical space, and a hairline transition into the
 * sidebar + canvas that follows.
 *
 * Props:
 *   eyebrow  – small-caps page identity label
 *   title    – page heading (h1)
 *   intro    – one locked intro sentence (optional)
 *   children – optional right-hand slot (e.g. a compact evidence/note frame)
 *   wide     – when true, use the wider spine-shell container (rail pages)
 */
import { MemoEyebrow } from "@/components/Patterns";
import { AnalyticalField } from "@/components/AnalyticalField";

/**
 * Masthead analytical terrain — the page/quiet variant of the shared field sits
 * behind the masthead so inner pages continue the hero's terrain DNA at reduced
 * intensity. `field` tunes the composition per page; default is a subtle upper
 * terrain. Radar and domain labels are NOT rendered here (variant !== "hero").
 */
export function PageMasthead({ eyebrow, title, intro, children, wide, field = "quiet", fieldFocal }) {
  return (
    <div className="relative border-b border-memo-line pt-7 pb-6 md:pt-10 md:pb-8 overflow-hidden">
      <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
      <AnalyticalField
        variant={field}
        focalPosition={fieldFocal}
        className="opacity-100"
        ariaLabel="Analytical terrain"
      />
      <div className={`relative z-10 ${wide ? "container-site spine-shell" : "container-site"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-end">
          <div>
            {eyebrow ? <MemoEyebrow>{eyebrow}</MemoEyebrow> : null}
            <h1
              className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.08] mt-3"
              style={{ color: "var(--memo-ink)" }}
            >
              {title}
            </h1>
            {intro ? (
              <p
                className="mt-3 text-[1.02rem] max-w-[62ch] leading-relaxed"
                style={{ color: "var(--memo-ink-2)" }}
              >
                {intro}
              </p>
            ) : null}
          </div>
          {children ? <div className="lg:justify-self-end lg:pb-1">{children}</div> : null}
        </div>
      </div>
    </div>
  );
}
